import { useContext, useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";

/** tiny format helpers kept local */
const fmt = {
  dash: (v) => (v === null || v === undefined || v === "" ? "—" : v),
};

export default function useWhatsapp() {
  const { api, api2, baseUrl, adminId, latitude, longitude } =
    useContext(GlobalContext);

  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({});

  const setSpin = (id, action) => setBusy((p) => ({ ...p, [id]: action }));
  const clearSpin = (id) => setBusy((p) => ({ ...p, [id]: null }));

  const getWhatsappNumbers = async (userid) => {
    try {
      const res = await api.post("/Whatsapp/v1.0/getWhatsappNumbers", {
        userid,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.response || [],
        };
      } else {
        return {
          success: false,
          message:
            res?.data?.responseMessage || "Failed to load WhatsApp numbers",
          data: [],
        };
      }
    } catch (e) {
      console.error("getWhatsappNumbers error:", e);
      return {
        success: false,
        message: "Error loading WhatsApp numbers",
        data: [],
      };
    }
  };
  const getWhatsappTemplates = async (userid) => {
    try {
      const res = await api.post("/Whatsapp/getTemplates", { userid });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.response?.data || [],
        };
      } else {
        return {
          success: false,
          data: [],
        };
      }
    } catch (e) {
      console.error("getWhatsappTemplates error:", e);
      return {
        success: false,
        data: [],
      };
    }
  };

  const deleteCampaign = async ({
    userid,
    campaignId,
    secret = "string",
    status = "string",
  }) => {
    try {
      const res = await api.post("/Campaign/deleteCampaign", {
        userid,
        secret,
        campaignId,
        status,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message: res?.data?.responseMessage || "Campaign deleted",
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to delete",
        };
      }
    } catch (e) {
      console.error("deleteCampaign error:", e);
      return {
        success: false,
        message: "Error deleting campaign",
      };
    }
  };

  const getAllCampaignsSuperAdmin = async (userid) => {
    try {
      const res = await api.post("/SuperAdmin/v1.0/getAllCampaigns", {
        userid,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.response || [],
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to load campaigns",
          data: [],
        };
      }
    } catch (e) {
      console.error("getAllCampaignsSuperAdmin error:", e);
      return {
        success: false,
        message: "Error loading campaigns",
        data: [],
      };
    }
  };
  const getWhatsappLogs = async ({ userid, campaignId, status }) => {
    try {
      const res = await api.post("/Whatsapp/v1.0/getWhatsappLogs", {
        userid,
        campaignId,
        status,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.response || [],
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to load logs",
          data: [],
        };
      }
    } catch (e) {
      console.error("getWhatsappLogs error:", e);
      return {
        success: false,
        message: "Error loading logs",
        data: [],
      };
    }
  };
  const getWhatsappTemplatesV1 = async ({ userid, phoneNumberId }) => {
    try {
      const res = await api.post("/Whatsapp/v1.0/getTemplates", {
        userid: String(userid),
        phoneNumberId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.response?.data || [],
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to load templates",
          data: [],
        };
      }
    } catch (e) {
      console.error("getWhatsappTemplatesV1 error:", e);
      return {
        success: false,
        message: "Error loading templates",
        data: [],
      };
    }
  };
  const deleteWhatsappTemplate = async ({
    userid,
    templateId,
    secret = "string",
  }) => {
    try {
      const res = await api.post("/Whatsapp/deleteTemplate", {
        userid,
        secret,
        templateId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          message: res?.data?.responseMessage || "Template deleted",
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to delete",
        };
      }
    } catch (e) {
      console.error("deleteTemplate error:", e);
      return {
        success: false,
        message: "Error deleting template",
      };
    }
  };

  const getCampaigns = async ({
    userid,
    campaignName = "string",
    phoneNumberId,
  }) => {
    try {
      const res = await api.post("/Campaign/v1.0/getCampaigns", {
        userid,
        campaignName,
        phoneNumberId,
      });

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res.data.data || [],
        };
      } else {
        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to load campaign",
          data: [],
        };
      }
    } catch (e) {
      console.error("getCampaigns error:", e);
      return {
        success: false,
        message: "Error loading campaign",
        data: [],
      };
    }
  };
  const getCampaignCharges = async (userid = "") => {
    try {
      const query = userid ? `?userid=${encodeURIComponent(userid)}` : "";
      const res = await api.post(`/Campaign/getCampaignCharges${query}`);

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || [],
          message: res?.data?.responseMessage || "Charges fetched successfully",
        };
      } else {
        return {
          success: false,
          data: [],
          message:
            res?.data?.responseMessage || "Failed to fetch campaign charges",
        };
      }
    } catch (e) {
      console.error("getCampaignCharges error:", e);
      return {
        success: false,
        data: [],
        message: "Error fetching campaign charges",
      };
    }
  };

  const insertUpdateCampiagnCharges = async ({
    type,
    pkid,
    key,
    value,
    userid,
  }) => {
    try {
      const payload = {
        type: String(type ?? ""),
        pkid: pkid ? String(pkid) : "",
        key: String(key ?? "").trim(),
        value: String(value ?? "").trim(),
        userid: userid ? String(userid) : "",
      };

      const res = await api.post(
        "/Campaign/v1.0/insertUpdateCampiagnCharges",
        payload,
      );

      if (res?.data?.responseCode === "00") {
        return {
          success: true,
          data: res?.data?.response || null,
          message:
            res?.data?.responseMessage || "Campaign charge saved successfully",
        };
      } else {
        return {
          success: false,
          data: null,
          message:
            res?.data?.responseMessage || "Failed to save campaign charge",
        };
      }
    } catch (e) {
      console.error("insertUpdateCampiagnCharges error:", e);
      return {
        success: false,
        data: null,
        message: "Error saving campaign charge",
      };
    }
  };
  const getWhatsappConversation = useCallback(
    async ({
      userid,
      contact,
      pageIndex = 1,
      recordPerPage = 20,
      secret = "",
    }) => {
      try {
        const res = await api.post("/Whatsapp/v1.0/getWhatsappConversation", {
          userid: String(userid || ""),
          secret: String(secret || ""),
          contact: String(contact || ""),
          pageIndex: String(pageIndex),
          recordPerPage: String(recordPerPage),
        });

        if (res?.data?.responseCode === "00") {
          return {
            success: true,
            data: Array.isArray(res?.data?.data) ? res.data.data : [],
            message:
              res?.data?.responseMessage || "Conversation fetched successfully",
          };
        }

        return {
          success: false,
          data: [],
          message: res?.data?.responseMessage || "Failed to fetch conversation",
        };
      } catch (e) {
        console.error("getWhatsappConversation error:", e);
        return {
          success: false,
          data: [],
          message: "Error fetching conversation",
        };
      }
    },
    [api],
  );

  const sendInstantMessage = useCallback(
    async ({ userid, contact, message, secret = "NA" }) => {
      try {
        const res = await api.post("/Whatsapp/sendInstantMessage", {
          userid: String(userid || ""),
          secret: String(secret || "NA"),
          message: String(message || "").trim(),
          contact: String(contact || ""),
        });

        if (res?.data?.responseCode === "00") {
          return {
            success: true,
            message: res?.data?.responseMessage || "Message sent successfully",
            data: res?.data || {},
          };
        }

        return {
          success: false,
          message: res?.data?.responseMessage || "Failed to send message",
          data: res?.data || {},
        };
      } catch (e) {
        console.error("sendInstantMessage error:", e);
        return {
          success: false,
          message: "Error sending message",
          data: {},
        };
      }
    },
    [api],
  );

  return {
    clients,
    orders,
    loading,
    busy,
    getWhatsappNumbers,
    getWhatsappTemplates,
    getCampaigns,
    deleteCampaign,
    getAllCampaignsSuperAdmin,
    getWhatsappLogs,
    getWhatsappTemplatesV1,
    deleteWhatsappTemplate,
    getCampaignCharges,
    insertUpdateCampiagnCharges,
    getWhatsappConversation,
    sendInstantMessage,
  };
}
